import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { KeyRound, Zap, Shield, BarChart3 } from "lucide-react"
import RequireRole from "../../common/validation/require-role"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants/navbar"
import { selectUserDetails } from "../../redux/slices/auth"
import { useAppSelector } from "../../common/hooks"
import { useIsSuperAdmin } from "../../common/hooks/use-user-roles"
import PageHeader from "../../common/ui/page-header"
import PillTabs from "../../common/ui/pill-tabs"
import ApiKeysSection from "./api-keys-section"
import ApiUsageSection from "./api-usage-section"

const TABS = [
  { id: "keys", label: "Keys" },
  { id: "usage", label: "Usage" }
]

const ApiPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const currentTab = searchParams.get("tab") || "keys"
  const user = useAppSelector(selectUserDetails)
  const isSuperAdmin = useIsSuperAdmin()
  const hasApiAccess = isSuperAdmin || !!user?.org?.api_access_active

  const handleTabChange = useCallback(
    (tabId: string) => {
      setSearchParams({ tab: tabId })
    },
    [setSearchParams]
  )

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["api"]}>
      <div className="p-6 space-y-6">
        <PageHeader title="API Dashboard" />
        {hasApiAccess ? (
          <>
            <PillTabs
              tabs={TABS}
              currentTab={currentTab}
              onChange={handleTabChange}
              className="justify-start px-0 pb-0"
            />
            {currentTab === "keys" && (
              <ApiKeysSection
                onViewUsage={(keyId: string) =>
                  setSearchParams({ tab: "usage", key: keyId })
                }
              />
            )}
            {currentTab === "usage" && (
              <ApiUsageSection initialKeyId={searchParams.get("key") || ""} />
            )}
          </>
        ) : (
          <ApiMarketingContent />
        )}
      </div>
    </RequireRole>
  )
}

const ApiMarketingContent = () => (
  <div className="max-w-3xl mx-auto py-8">
    <div className="text-center mb-10">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 mb-4">
        <KeyRound size={32} className="text-primary-400" />
      </div>
      <h2 className="text-2xl font-serif font-semibold text-neutral-800 mb-3">
        BankingBridge API
      </h2>
      <p className="text-neutral-500 text-base max-w-lg mx-auto">
        Integrate BankingBridge directly into your applications with our REST
        API. Manage rates, leads, subscriptions, and more programmatically.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mb-3">
          <Zap size={20} className="text-green-600" />
        </div>
        <h3 className="font-semibold text-neutral-800 mb-1">Real-time Data</h3>
        <p className="text-sm text-neutral-500">
          Access live rate data, leads, and subscription information through
          simple API calls.
        </p>
      </div>
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mb-3">
          <Shield size={20} className="text-blue-600" />
        </div>
        <h3 className="font-semibold text-neutral-800 mb-1">Secure Access</h3>
        <p className="text-sm text-neutral-500">
          API key authentication with usage tracking and monitoring for every
          request.
        </p>
      </div>
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center mb-3">
          <BarChart3 size={20} className="text-purple-600" />
        </div>
        <h3 className="font-semibold text-neutral-800 mb-1">Usage Analytics</h3>
        <p className="text-sm text-neutral-500">
          Monitor daily usage, track endpoint activity, and manage your API keys
          from one dashboard.
        </p>
      </div>
    </div>

    <div className="text-center">
      <p className="text-sm text-neutral-500">
        Contact your account manager to enable API access for your organization.
      </p>
    </div>
  </div>
)

export default ApiPage
